import type { ReactNode } from 'react';
import { Box, type SxProps, type Theme, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

const HEIGHT = 40;
const OUTLINE_TOP_OFFSET = -5;
const LEGEND_HEIGHT = 11;
const LABEL_LEFT = 10;

export const notchedOutlineColor = (theme: Theme) =>
  alpha(theme.palette.text.primary, 0.23);

export type NotchedOutlinedFieldProps = {
  label?: string;
  children: ReactNode;
  sx?: SxProps<Theme>;
};

/**
 * Outlined shell for custom controls that should match MUI OutlinedInput.
 * Pass `label` for a notched floating label; omit it for a closed outline.
 */
const NotchedOutlinedField = ({
  label,
  children,
  sx,
}: NotchedOutlinedFieldProps) => (
  <Box
    sx={[
      {
        position: 'relative',
        width: '100%',
        mt: 0.75,
        height: HEIGHT,
        '&:hover .notched-outlined-field__outline': {
          borderColor: 'text.primary',
        },
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
  >
    {label ? (
      <Typography
        component="span"
        sx={{
          position: 'absolute',
          top: 0,
          left: LABEL_LEFT,
          zIndex: 3,
          transform: 'translateY(-50%)',
          px: 0.5,
          fontSize: '0.75em',
          lineHeight: 1,
          color: 'text.secondary',
          pointerEvents: 'none',
        }}
      >
        {label}
      </Typography>
    ) : null}

    <Box
      component="fieldset"
      aria-hidden
      className="notched-outlined-field__outline"
      sx={{
        textAlign: 'left',
        position: 'absolute',
        inset: 0,
        top: OUTLINE_TOP_OFFSET,
        m: 0,
        px: 1,
        py: 0,
        pointerEvents: 'none',
        borderRadius: 1,
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: notchedOutlineColor,
        overflow: 'hidden',
        minWidth: '0%',
        transition: 'border-color 0.15s ease',
      }}
    >
      <Box
        component="legend"
        sx={{
          float: 'unset',
          width: 'auto',
          overflow: 'hidden',
          display: 'block',
          p: 0,
          height: LEGEND_HEIGHT,
          fontSize: '0.75em',
          visibility: 'hidden',
          maxWidth: label ? '100%' : '0.01px',
          whiteSpace: 'nowrap',
          '& > span': {
            px: 0.625,
            display: 'inline-block',
            opacity: 0,
            visibility: 'visible',
          },
        }}
      >
        {label ? <span>{label}</span> : null}
      </Box>
    </Box>

    <Box
      sx={{
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        alignItems: 'stretch',
        height: '100%',
      }}
    >
      {children}
    </Box>
  </Box>
);

export default NotchedOutlinedField;
